import { useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { retryBookAnchor, swapCastPhoto } from '../../api';
import { usePhotoUpload } from '../../use-photo-upload';
import type { EnvelopeImageRefusalCastPhoto, PreviewEnvelope } from '../../types';
import { FotoUploadSlot } from '../personen/FotoUploadSlot';
import type { FotoUploadSlotState } from '../personen/FotoUploadSlot';

/**
 * Sebastian correction slice 2026-09-10 — image_refused screen with
 * two branches (content_refused vs technical) driven by
 * `envelope.intake.derived.imageRefusal.cause`.
 *
 * ═══ CAUSE BRANCH ═══════════════════════════════════════════════════
 *
 * `cause === 'content_refused'` (or absent for pre-slice books) —
 * Photo-swap flow from Paket 3 Item 1. Header + per-cast photo-swap
 * rows. The swap-affordance is GATED on
 * `envelope.photoSwapUsed === false` (Part 1.2 one-swap-per-book cap)
 * AND on `stage === 'book_anchor'`. When photoSwapUsed is true, only
 * the header renders — no swap section, no cast list.
 *
 * `cause === 'technical'` — Simpler retry flow. Header + centered
 * "Nochmal zeichnen" button. Click fires POST /anchor-retry which
 * clears the imageRefusal, transitions image_refused → anchor_pending,
 * and enqueues a fresh anchor render.
 *
 * ═══ DIALOG GATE (Part 2.4) ═════════════════════════════════════════
 *
 * On content_refused book_anchor, clicking "Foto ändern" on any cast
 * row opens the shared photo-swap dialog ("So wird die Figur gut")
 * BEFORE the FotoUploadSlot's file picker. Customer confirms by
 * clicking "Verstanden"; cancel closes without opening the picker.
 * Dialog shared with AnchorReviewScreen's voluntary-swap flow.
 */

const BUTTON_GOLD =
  'h-12 rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const ERROR_CLASS = 'text-sm text-error';

export interface ImageRefusedScreenProps {
  readonly envelope: PreviewEnvelope;
  readonly onRefetch: () => void;
}

type SwapPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'uploading' }
  | { readonly kind: 'error' };

type RetryPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'error' };

interface PickedFile {
  readonly file: File;
  readonly previewUrl: string;
}

interface OpenSlot {
  readonly castKey: string;
  readonly picked: PickedFile | null;
}

export function ImageRefusedScreen({ envelope, onRefetch }: ImageRefusedScreenProps) {
  const stage = envelope.intake?.derived?.imageRefusal?.stage ?? 'book_anchor';
  // Sebastian correction slice 2026-09-10 (Part 2.1) — cause branch.
  // Absent defaults to 'content_refused' for backward compat with
  // pre-slice envelopes.
  const cause = envelope.intake?.derived?.imageRefusal?.cause ?? 'content_refused';
  const photoSwapUsed = envelope.photoSwapUsed ?? false;

  const contentRefusedCopy = STRINGS.book.status.imageRefused[stage];
  const technicalCopy = STRINGS.book.status.imageRefused.book_anchor.technical;
  const supportLine = STRINGS.book.status.imageRefused.supportLine;

  // For the technical branch we always use the technical copy (stage
  // === 'book_anchor' is the only stage that fires cause === 'technical'
  // today; forward-compat is content_refused-only for cover / scene).
  const isTechnical = stage === 'book_anchor' && cause === 'technical';
  const heading = isTechnical ? technicalCopy.heading : contentRefusedCopy.heading;
  const body = isTechnical ? technicalCopy.body : contentRefusedCopy.body;

  const castPhotos: readonly EnvelopeImageRefusalCastPhoto[] =
    envelope.intake?.derived?.imageRefusal?.castPhotos ?? [];

  // Which row's upload slot is open, and what file (if any) is
  // currently picked for that row. Only one row can be open at a
  // time — click "Foto ändern" on a different row closes the current.
  const [openSlot, setOpenSlot] = useState<OpenSlot | null>(null);
  const [phase, setPhase] = useState<SwapPhase>({ kind: 'idle' });
  const photoUpload = usePhotoUpload();

  // Dialog + retry state.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingCastKey, setPendingCastKey] = useState<string | null>(null);
  const [retryPhase, setRetryPhase] = useState<RetryPhase>({ kind: 'idle' });

  const openDialogForCastKey = (castKey: string) => {
    setPendingCastKey(castKey);
    setDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setPendingCastKey(null);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    // Confirmed → open the FotoUploadSlot for the pending castKey.
    if (pendingCastKey !== null) {
      // If a preview URL is in play for a different row, revoke it.
      if (openSlot !== null && openSlot.picked !== null && openSlot.castKey !== pendingCastKey) {
        URL.revokeObjectURL(openSlot.picked.previewUrl);
      }
      setOpenSlot({ castKey: pendingCastKey, picked: null });
      setPhase({ kind: 'idle' });
      setPendingCastKey(null);
    }
  };

  const handleFileSelected = (file: File) => {
    if (openSlot === null) return;
    if (openSlot.picked !== null) URL.revokeObjectURL(openSlot.picked.previewUrl);
    setOpenSlot({
      castKey: openSlot.castKey,
      picked: { file, previewUrl: URL.createObjectURL(file) },
    });
  };

  const handleRemove = () => {
    if (openSlot === null) return;
    if (openSlot.picked !== null) URL.revokeObjectURL(openSlot.picked.previewUrl);
    setOpenSlot({ castKey: openSlot.castKey, picked: null });
    setPhase({ kind: 'idle' });
  };

  const runSwap = async () => {
    if (openSlot === null || openSlot.picked === null) return;
    setPhase({ kind: 'uploading' });
    try {
      const [key] = await photoUpload.upload([openSlot.picked.file]);
      if (key === undefined) {
        throw new Error('image-refused swap: upload returned no key');
      }
      await swapCastPhoto(envelope.bookOrderId, openSlot.castKey, {
        whichPhoto: 'face',
        newPhotoKey: key,
      });
      onRefetch();
    } catch (err) {
      setPhase({ kind: 'error' });
      console.warn('[image-refused] swap failed', err);
    }
  };

  const runTechnicalRetry = async () => {
    setRetryPhase({ kind: 'pending' });
    try {
      await retryBookAnchor(envelope.bookOrderId);
      onRefetch();
    } catch (err) {
      setRetryPhase({ kind: 'error' });
      console.warn('[image-refused] technical retry failed', err);
    }
  };

  // Show swap affordance ONLY on content_refused book_anchor stage
  // AND when the customer has not yet used the one swap allowance.
  // Sebastian correction slice 2026-09-10 (Part 1.2 cap + Part 2.1
  // cause branch).
  const showSwapAffordance = !isTechnical && stage === 'book_anchor' && !photoSwapUsed;

  const slotState: FotoUploadSlotState =
    openSlot === null || openSlot.picked === null
      ? { kind: 'empty' }
      : { kind: 'picked', previewUrl: openSlot.picked.previewUrl };

  return (
    <div className="space-y-6 py-6" data-testid="image-refused-screen">
      {/* Header — heading + body + support line. Shape unchanged
          across cause branches; only the copy source differs. */}
      <div className="flex flex-col items-center">
        <h1 className="mb-4 text-center text-base font-semibold text-dream-navy">{heading}</h1>
        <p className="text-center text-base text-dream-navy">{body}</p>
        <p className="mt-4 text-center text-base text-dream-navy">{supportLine}</p>
      </div>

      {/* Technical branch — centered "Nochmal zeichnen" button.
          POST /anchor-retry clears the imageRefusal + transitions
          image_refused → anchor_pending + enqueues a fresh anchor
          render. Envelope re-polls; BookRoute re-dispatches. */}
      {isTechnical && (
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => void runTechnicalRetry()}
            disabled={retryPhase.kind === 'pending'}
            className={BUTTON_GOLD}
          >
            {technicalCopy.retryButtonLabel}
          </button>
          {retryPhase.kind === 'error' && (
            <p className={ERROR_CLASS}>{STRINGS.book.photoRejected.swapErrorPlaceholder}</p>
          )}
        </div>
      )}

      {/* Content-refused book_anchor branch — photo-swap section.
          Gated on `stage === 'book_anchor'` AND `!photoSwapUsed`.
          Cover + scene stages (forward-compat, no cause=content_refused
          fire today via image_refused) render header only. */}
      {showSwapAffordance && castPhotos.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-center text-base font-semibold text-dream-navy">
            {STRINGS.book.imageRefused.swapSectionHeading}
          </h2>

          <ul className="space-y-4">
            {castPhotos.map((entry) => {
              const isOpen = openSlot !== null && openSlot.castKey === entry.castKey;
              return (
                <li key={entry.castKey} className="space-y-3">
                  <div className="flex items-center gap-3">
                    {entry.facePhotoUrl !== null && (
                      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-dream-navy/10">
                        <img
                          src={entry.facePhotoUrl}
                          alt={STRINGS.book.photoRejected.rejectedTileAria}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base text-dream-navy">{entry.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openDialogForCastKey(entry.castKey)}
                      disabled={phase.kind === 'uploading'}
                      className="shrink-0 rounded-lg border border-dream-navy/30 px-4 py-2 text-sm text-dream-navy hover:bg-dream-background disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-dream-navy/50"
                    >
                      {STRINGS.book.imageRefused.swapButtonLabel}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="space-y-3">
                      <FotoUploadSlot
                        state={slotState}
                        onFileSelected={handleFileSelected}
                        onRemove={handleRemove}
                        showRules={true}
                        ariaLabel={STRINGS.book.photoRejected.slotAriaEmpty}
                        castName={entry.name}
                      />

                      {phase.kind === 'uploading' && (
                        <p className="text-sm text-dream-navy/70">
                          {STRINGS.book.photoRejected.uploadingHeading}
                        </p>
                      )}

                      {phase.kind === 'error' && (
                        <p className={ERROR_CLASS}>
                          {STRINGS.book.photoRejected.swapErrorPlaceholder}
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={() => void runSwap()}
                        disabled={openSlot?.picked === null || phase.kind === 'uploading'}
                        className={`${BUTTON_GOLD} w-full`}
                      >
                        {phase.kind === 'error'
                          ? STRINGS.book.photoRejected.retryCta
                          : STRINGS.book.personen.blockWeiterLabel}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <PhotoSwapDialog
        open={dialogOpen}
        onCancel={handleDialogCancel}
        onConfirm={handleDialogConfirm}
      />
    </div>
  );
}

/**
 * Sebastian correction slice 2026-09-10 (Part 2.4) — shared photo-swap
 * dialog. Fires BEFORE the file picker opens on BOTH the
 * ImageRefusedScreen swap flow AND the AnchorReviewScreen voluntary-
 * swap flow. Copy released verbatim in `STRINGS.book.photoSwapDialog`.
 *
 * Radix Dialog with backdrop, ESC / click-outside dismisses. Cancel
 * (backdrop / ESC) closes without opening the file picker; confirm
 * ("Verstanden") closes and the caller proceeds to open the
 * FotoUploadSlot's file selector.
 *
 * Exported so the AnchorReviewScreen (Part 2.3) can reuse the exact
 * same dialog without duplicating copy or shape.
 */
export interface PhotoSwapDialogProps {
  readonly open: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function PhotoSwapDialog({ open, onCancel, onConfirm }: PhotoSwapDialogProps) {
  const dialog = STRINGS.book.photoSwapDialog;
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="text-lg font-semibold text-dream-navy pr-6">
          {dialog.heading}
        </DialogTitle>
        <DialogDescription className="sr-only">{dialog.heading}</DialogDescription>
        <ul className="space-y-2 text-base text-dream-navy">
          {dialog.bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-2">
              <span aria-hidden className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-dream-navy" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-center pt-2">
          <button type="button" onClick={onConfirm} className={BUTTON_GOLD}>
            {dialog.confirmButton}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
